import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import type { FilterMode } from './types';
import { MONTHS_FR } from './types';

interface PeriodSelectorProps {
  filterMode: FilterMode;
  setFilterMode: (mode: FilterMode) => void;
  selectedYear: number;
  setSelectedYear: (year: number) => void;
  selectedMonth: number;
  setSelectedMonth: (month: number) => void;
  selectedWeek: number;
  setSelectedWeek: (week: number) => void;
  handlePrevious: () => void;
  handleNext: () => void;
  handleToday: () => void;
  periodLabel: string;
}

export default function PeriodSelector({
  filterMode, setFilterMode,
  selectedYear, setSelectedYear,
  selectedMonth, setSelectedMonth,
  selectedWeek, setSelectedWeek,
  handlePrevious, handleNext, handleToday,
  periodLabel
}: PeriodSelectorProps) {
  return (
    <div style={{
      background: 'white',
      borderRadius: '16px',
      padding: '20px',
      border: '1px solid #e2e8f0',
      marginBottom: '24px'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
        {/* Sélecteur de mode */}
        <div style={{ display: 'flex', gap: '4px', background: '#f1f5f9', padding: '4px', borderRadius: '10px' }}>
          {(['week', 'month', 'year'] as FilterMode[]).map(mode => (
            <button
              key={mode}
              onClick={() => setFilterMode(mode)}
              style={{
                padding: '8px 16px',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '0.85rem',
                background: filterMode === mode ? 'linear-gradient(135deg, #2563eb 0%, #4f46e5 100%)' : 'transparent',
                color: filterMode === mode ? 'white' : '#64748b',
                transition: 'all 0.2s',
                boxShadow: filterMode === mode ? '0 2px 4px rgba(37, 99, 235, 0.3)' : 'none'
              }}
            >
              {mode === 'week' ? 'Semaine' : mode === 'month' ? 'Mois' : 'Année'}
            </button>
          ))}
        </div>

        {/* Navigation */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: '8px',
          background: '#f8fafc', padding: '8px 12px', borderRadius: '10px', border: '1px solid #e2e8f0'
        }}>
          <button
            onClick={handlePrevious}
            style={{
              padding: '8px', background: 'white', border: '1px solid #e2e8f0',
              borderRadius: '8px', cursor: 'pointer', color: '#64748b',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}
          >
            <ChevronLeft size={18} />
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: '220px', justifyContent: 'center' }}>
            <Calendar size={18} color="#2563eb" />
            <span style={{ fontWeight: '700', color: '#1e293b', fontSize: '0.95rem' }}>{periodLabel}</span>
          </div>
          <button
            onClick={handleNext}
            style={{
              padding: '8px', background: 'white', border: '1px solid #e2e8f0',
              borderRadius: '8px', cursor: 'pointer', color: '#64748b',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}
          >
            <ChevronRight size={18} />
          </button>
        </div>

        {/* Sélecteurs détaillés */}
        <div style={{ display: 'flex', gap: '8px' }}>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            style={{
              padding: '10px 14px', borderRadius: '8px',
              border: '1px solid #e2e8f0', fontSize: '0.9rem',
              cursor: 'pointer', background: 'white'
            }}
          >
            {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i).map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>

          {(filterMode === 'month' || filterMode === 'week') && (
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
              style={{
                padding: '10px 14px', borderRadius: '8px',
                border: '1px solid #e2e8f0', fontSize: '0.9rem',
                cursor: 'pointer', background: 'white', minWidth: '120px'
              }}
            >
              {MONTHS_FR.map((m, idx) => (
                <option key={idx} value={idx}>{m}</option>
              ))}
            </select>
          )}

          {filterMode === 'week' && (
            <select
              value={selectedWeek}
              onChange={(e) => setSelectedWeek(parseInt(e.target.value))}
              style={{
                padding: '10px 14px', borderRadius: '8px',
                border: '1px solid #e2e8f0', fontSize: '0.9rem',
                cursor: 'pointer', background: 'white'
              }}
            >
              {Array.from({ length: 53 }, (_, i) => i + 1).map(w => (
                <option key={w} value={w}>S{w}</option>
              ))}
            </select>
          )}
        </div>

        {/* Bouton Aujourd'hui */}
        <button
          onClick={handleToday}
          style={{
            padding: '10px 16px',
            background: '#f0fdf4',
            border: '1px solid #bbf7d0',
            borderRadius: '8px',
            color: '#15803d',
            fontWeight: '600',
            fontSize: '0.85rem',
            cursor: 'pointer'
          }}
        >
          Aujourd'hui
        </button>
      </div>
    </div>
  );
}
